// Components for displaying an avatar of someone, with support for Gravatar
// API.
import { UserCircleIcon } from "@heroicons/react/20/solid";
import { FC, useState } from "react";
import { cn } from "../lib/text-utils";

interface IGravatarAvatar {
  // This is the hash of the user's email.
  profile_identifier: string;

  // This is the desired image size in pixels for one edge of the square image.
  desired_image_size?: number;

  // Any additional class names or class name overrides to be passed to cn().
  className?: string;
}

export const GravatarAvatar: FC<IGravatarAvatar> = ({
  profile_identifier,
  desired_image_size = 32,
  className = "",
}) => {
  // Want to show the user's Gravatar image, but only if they have a registered
  // avatar on Gravatar. Otherwise, show a generic icon instead of the ugly
  // default Gravatar image.

  // Note that we should generally use the same desired_image_size parameter
  // throughout the application in order to exploit caching when repeatedly
  // showing the same profile pictures.

  // Note that the Gravatar profiles API, where I previously tried to get this
  // information, has rate limits of 100 requests/hour if unauthenticated, and
  // only 1k requests/hr if authenticated. Instead, we should use the Gravatar
  // image API, which fetches images directly and doesn't have those rate
  // limits (see https://docs.gravatar.com/sdk/images/). We can configure a
  // default image to be shown in the case the user doesn't have a profile or
  // doesn't have an image uploaded. Or, even better, we can have the request
  // error out because we can handle this error to conditionally render our own
  // icon instead.

  // Construct the URL used to load the image. We want an image of the correct
  // desired size, and also want to force a 404 error if the Gravatar image
  // doesn't exist (either because the user does not have a Gravatar profile,
  // or because the user does have a Gravatar profile but has not uploaded an
  // image).
  const url = `https://gravatar.com/avatar/${profile_identifier}?s=${desired_image_size}&d=404`;

  // Add handlers to allow the image tag itself to be able to resolve 404
  // errors. Thanks to https://stackoverflow.com/a/51401156. Begin by assuming
  // a valid profile image exists.
  const [userHasImage, setUserHasImage] = useState(true);

  return userHasImage ? (
    <img
      className={cn("rounded-full", className)}
      src={url}
      // If fetching this image URL results in a 404, switch to showing icon
      // instead.
      onError={() => setUserHasImage(false)}
    />
  ) : (
    <UserCircleIcon
      className={cn(
        "block text-slate-300 dark:text-slate-400 w-7 h-7",
        className
      )}
    />
  );
};
